import { useState } from 'react';
import { X, CheckCircle2, Ruler, MousePointer2, Palette, Move } from 'lucide-react';
import { V1VerificationPanel } from '../editor/V1VerificationPanel';
import { TextMeasurementDiagnostic } from '../editor/TextMeasurementDiagnostic';
import { ButtonGeometryDiagnostic } from '../ButtonGeometryDiagnostic';
import { ButtonColorDiagnostic } from '../editor/ButtonColorDiagnostic';
import { GroupPositioningDiagnostic } from '../editor/GroupPositioningDiagnostic';

type DiagnosticTab = 'verification' | 'measurement' | 'geometry' | 'button-color' | 'group-position';

interface DiagnosticWidgetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DiagnosticWidget({ isOpen, onClose }: DiagnosticWidgetProps) {
  const [activeTab, setActiveTab] = useState<DiagnosticTab>('verification');

  if (!isOpen) return null;

  const tabs: Array<{ id: DiagnosticTab; label: string; Icon: typeof CheckCircle2 }> = [
    { id: 'verification', label: 'V1 Verify', Icon: CheckCircle2 },
    { id: 'measurement', label: 'Text Measure', Icon: Ruler },
    { id: 'geometry', label: 'Button Geom', Icon: MousePointer2 },
    { id: 'button-color', label: 'Btn Color', Icon: Palette },
    { id: 'group-position', label: 'Group Pos', Icon: Move },
  ];

  return (
    <div className="fixed top-12 left-1/2 -translate-x-1/2 z-[140] w-[460px] bg-zinc-900/95 backdrop-blur-sm border border-zinc-700 rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.6)] overflow-hidden">
      <div className="flex items-center justify-between border-b border-zinc-700 bg-zinc-950/60 px-2 py-1">
        <div className="flex gap-1">
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 rounded px-2 py-1 text-xs transition-colors ${
                activeTab === id
                  ? 'bg-zinc-700 text-cyan-400'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
              }`}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-red-400"
          aria-label="Close diagnostics"
        >
          <X size={14} />
        </button>
      </div>

      <div className="max-h-[80vh] overflow-y-auto">
        <div style={{ display: activeTab === 'verification' ? 'block' : 'none' }}>
          <V1VerificationPanel />
        </div>
        <div style={{ display: activeTab === 'measurement' ? 'block' : 'none' }}>
          <TextMeasurementDiagnostic />
        </div>
        <div style={{ display: activeTab === 'geometry' ? 'block' : 'none' }}>
          <ButtonGeometryDiagnostic />
        </div>
        <div style={{ display: activeTab === 'button-color' ? 'block' : 'none' }}>
          <ButtonColorDiagnostic />
        </div>
        <div style={{ display: activeTab === 'group-position' ? 'block' : 'none' }}>
          <GroupPositioningDiagnostic />
        </div>
      </div>
    </div>
  );
}
